import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import {
  faCloudSun,
  faDroplet,
  faCloudRain,
  faCloudShowersHeavy,
  faBolt,
  faSnowflake,
  faSmog,
  faSun,
  faMoon,
} from '@fortawesome/pro-regular-svg-icons';

import { CurrentWeather, HourlyForecast, ParsedCoords, WeatherResponse } from './types';

export type WeatherUnitSystem = 'metric' | 'us';
export const WEATHER_UNIT_STORAGE_KEY = 'weather:unit-system';

// ── WMO weather-code lookup table ────────────────────────────────────────────

interface WmoMeta {
  description: string;
  descriptionNight?: string;
  icon: IconDefinition;
  iconNight?: IconDefinition;
  toneClass: string;
  toneClassNight?: string;
}

const WMO: Record<number, WmoMeta> = {
  0: {
    description: 'Clear sky', descriptionNight: 'Clear night',
    icon: faSun, iconNight: faMoon,
    toneClass: 'sun', toneClassNight: 'moon',
  },
  1: {
    description: 'Mostly clear', descriptionNight: 'Mostly clear night',
    icon: faCloudSun, toneClass: 'partly',
  },
  2:  { description: 'Partly cloudy', icon: faCloudSun, toneClass: 'partly' },
  3:  { description: 'Overcast', icon: faCloudSun, toneClass: 'overcast' },
  45: { description: 'Fog', icon: faSmog, toneClass: 'fog' },
  48: { description: 'Fog', icon: faSmog, toneClass: 'fog' },
  51: { description: 'Drizzle', icon: faDroplet, toneClass: 'drizzle' },
  53: { description: 'Drizzle', icon: faDroplet, toneClass: 'drizzle' },
  55: { description: 'Drizzle', icon: faDroplet, toneClass: 'drizzle' },
  56: { description: 'Freezing drizzle', icon: faDroplet, toneClass: 'drizzle' },
  57: { description: 'Freezing drizzle', icon: faDroplet, toneClass: 'drizzle' },
  61: { description: 'Rain', icon: faCloudRain, toneClass: 'rain' },
  63: { description: 'Rain', icon: faCloudRain, toneClass: 'rain' },
  65: { description: 'Rain', icon: faCloudRain, toneClass: 'rain' },
  66: { description: 'Freezing rain', icon: faCloudShowersHeavy, toneClass: 'freezing' },
  67: { description: 'Freezing rain', icon: faCloudShowersHeavy, toneClass: 'freezing' },
  71: { description: 'Snow', icon: faSnowflake, toneClass: 'snow' },
  73: { description: 'Snow', icon: faSnowflake, toneClass: 'snow' },
  75: { description: 'Snow', icon: faSnowflake, toneClass: 'snow' },
  77: { description: 'Snow grains', icon: faSnowflake, toneClass: 'snow' },
  80: { description: 'Rain showers', icon: faCloudShowersHeavy, toneClass: 'rain' },
  81: { description: 'Rain showers', icon: faCloudShowersHeavy, toneClass: 'rain' },
  82: { description: 'Rain showers', icon: faCloudShowersHeavy, toneClass: 'rain' },
  85: { description: 'Snow showers', icon: faSnowflake, toneClass: 'snow' },
  86: { description: 'Snow showers', icon: faSnowflake, toneClass: 'snow' },
  95: { description: 'Thunderstorm', icon: faBolt, toneClass: 'storm' },
  96: { description: 'Thunderstorm with hail', icon: faBolt, toneClass: 'storm' },
  99: { description: 'Thunderstorm with hail', icon: faBolt, toneClass: 'storm' },
};

const WMO_DEFAULT: WmoMeta = {
  description: 'Weather unavailable',
  icon: faCloudSun,
  toneClass: 'partly',
};

function resolveDescription(meta: WmoMeta, isDay: boolean): string {
  return isDay || !meta.descriptionNight ? meta.description : meta.descriptionNight;
}

function resolveIcon(meta: WmoMeta, isDay: boolean): IconDefinition {
  return isDay || !meta.iconNight ? meta.icon : meta.iconNight;
}

function resolveToneClass(meta: WmoMeta, isDay: boolean): string {
  return isDay || !meta.toneClassNight ? meta.toneClass : meta.toneClassNight;
}

export function weatherDescription(weatherCode: number | null, isDay: boolean): string {
  const meta = WMO[weatherCode ?? -1] ?? WMO_DEFAULT;
  return resolveDescription(meta, isDay);
}

export function weatherIcon(weatherCode: number | null, isDay: boolean): IconDefinition {
  const meta = WMO[weatherCode ?? -1] ?? WMO_DEFAULT;
  return resolveIcon(meta, isDay);
}

export function weatherToneClass(weatherCode: number | null, isDay: boolean): string {
  const meta = WMO[weatherCode ?? -1] ?? WMO_DEFAULT;
  return resolveToneClass(meta, isDay);
}

// ── Coordinate helpers ───────────────────────────────────────────────────────

export function parseCoords(input: string): ParsedCoords | null {
  const cleaned = input.trim();
  if (!cleaned) return null;

  const parts = cleaned.split(/[\s,]+/).filter(Boolean);
  if (parts.length < 2) return null;

  const latitude = Number(parts[0]);
  const longitude = Number(parts[1]);

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  if (latitude < -90 || latitude > 90) return null;
  if (longitude < -180 || longitude > 180) return null;

  return { latitude, longitude };
}

export function formatCoords({ latitude, longitude }: ParsedCoords) {
  return `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
}

export function metricIconClass(metric: 'temperature' | 'feelsLike' | 'humidity' | 'wind' | 'direction' | 'precipitation') {
  return `weather-sidebar__metric-icon--${metric}`;
}

function localeRegion(locale: string) {
  try {
    return new Intl.Locale(locale).region ?? null;
  } catch {
    return locale.match(/-([A-Z]{2})\b/i)?.[1]?.toUpperCase() ?? null;
  }
}

export function preferredWeatherUnitSystem(locale?: string) {
  const browserLocale = locale ?? globalThis.navigator?.language ?? '';
  return (localeRegion(browserLocale) === 'US') ? 'us' : 'metric';
}

export function isWeatherUnitSystem(value: unknown): value is WeatherUnitSystem {
  return value === 'metric' || value === 'us';
}

export function storedWeatherUnitSystem(storage?: Pick<Storage, 'getItem'> | null): WeatherUnitSystem | null {
  try {
    const source = storage ?? globalThis.localStorage;
    const storedValue = source.getItem(WEATHER_UNIT_STORAGE_KEY);
    return isWeatherUnitSystem(storedValue) ? storedValue : null;
  } catch {
    return null;
  }
}

export function saveWeatherUnitSystem(
  unitSystem: WeatherUnitSystem,
  storage?: Pick<Storage, 'setItem'> | null
) {
  try {
    const target = storage ?? globalThis.localStorage;
    target.setItem(WEATHER_UNIT_STORAGE_KEY, unitSystem);
  } catch {
    // Ignore storage write failures (for example private browsing restrictions).
  }
}

export function resolveWeatherUnitSystem(
  locale?: string,
  storage?: Pick<Storage, 'getItem'> | null
): WeatherUnitSystem {
  return storedWeatherUnitSystem(storage) ?? preferredWeatherUnitSystem(locale);
}

// ── API ──────────────────────────────────────────────────────────────────────

export async function fetchOpenMeteoWeather(
  coords: ParsedCoords,
  unitSystem: WeatherUnitSystem = 'metric'
): Promise<WeatherResponse> {
  const isUsUnits = unitSystem === 'us';
  const url = new URL('https://api.open-meteo.com/v1/forecast');
  url.searchParams.set('latitude', String(coords.latitude));
  url.searchParams.set('longitude', String(coords.longitude));
  url.searchParams.set('current', [
    'temperature_2m',
    'apparent_temperature',
    'relative_humidity_2m',
    'precipitation',
    'weather_code',
    'wind_speed_10m',
    'wind_direction_10m',
    'is_day',
  ].join(','));
  url.searchParams.set('hourly', [
    'temperature_2m',
    'weather_code',
    'precipitation_probability',
    'wind_speed_10m',
  ].join(','));
  url.searchParams.set('timezone', 'auto');
  url.searchParams.set('temperature_unit', isUsUnits ? 'fahrenheit' : 'celsius');
  url.searchParams.set('wind_speed_unit', isUsUnits ? 'mph' : 'kmh');
  url.searchParams.set('precipitation_unit', isUsUnits ? 'inch' : 'mm');

  const response = await fetch(url.toString());
  if (!response.ok) {
    throw new Error(`Open-Meteo request failed with status ${response.status}`);
  }

  return response.json() as Promise<WeatherResponse>;
}

// ── Data mappers ─────────────────────────────────────────────────────────────

export function toCurrentWeather(payload: WeatherResponse): CurrentWeather | null {
  if (!payload.current) return null;

  return {
    time: payload.current.time,
    temperature: payload.current.temperature_2m ?? null,
    apparentTemperature: payload.current.apparent_temperature ?? null,
    humidity: payload.current.relative_humidity_2m ?? null,
    precipitation: payload.current.precipitation ?? null,
    weatherCode: payload.current.weather_code ?? null,
    windSpeed: payload.current.wind_speed_10m ?? null,
    windDirection: payload.current.wind_direction_10m ?? null,
    isDay: payload.current.is_day === 1,
  };
}

export function toHourlyForecast(payload: WeatherResponse): HourlyForecast[] {
  const hourly = payload.hourly;
  if (!hourly?.time?.length) return [];

  const startIndex = hourly.time.findIndex((time) => new Date(time).getTime() >= Date.now());
  const forecastStart = startIndex >= 0 ? startIndex : 0;

  return hourly.time.slice(forecastStart, forecastStart + 8).map((time, index) => ({
    time,
    temperature: hourly.temperature_2m?.[forecastStart + index] ?? null,
    weatherCode: hourly.weather_code?.[forecastStart + index] ?? null,
    precipitationProbability: hourly.precipitation_probability?.[forecastStart + index] ?? null,
    windSpeed: hourly.wind_speed_10m?.[forecastStart + index] ?? null,
  }));
}

// ── Formatting helpers ───────────────────────────────────────────────────────

export function formatWindDirection(direction: number | null) {
  if (direction === null || !Number.isFinite(direction)) return '—';
  const degrees = ((direction % 360) + 360) % 360;
  const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  const index = Math.round(degrees / 45) % directions.length;
  return `${directions[index]} (${Math.round(degrees)}°)`;
}

export function formatUpdatedTime(time: string) {
  const date = new Date(time);
  if (Number.isNaN(date.getTime())) return time;

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

export function formatHourLabel(time: string) {
  const date = new Date(time);
  if (Number.isNaN(date.getTime())) return time;

  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    hour12: false,
  }).format(date);
}
